import { api, type ProgrammeContext, type ProgrammeTaskRow, type WbContext } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { esc, fmtDate, html } from '../ui';

const BASIS_LABEL: Record<string, string> = {
  ML_APPROVED_BY_ORG: 'AMP approved by this organisation — ML.A.302(b)',
  ML_MIP: 'Minimum inspection programme — ML.A.302(d)',
  ML_DECLARED: 'Owner-declared programme — ML.A.302(b)(2)',
  M_APPROVED: 'Approved AMP — M.A.302',
  FAA_91_409A: 'Annual + 100-hour — 91.409(a)',
  FAA_PROGRESSIVE: 'Progressive inspection — 91.409(d)',
  ANNEX_I_ACCEPTED: 'Accepted by the national authority',
  ANNEX_I_DECLARED: 'Owner-declared, where the state allows it',
};

/**
 * Revising the maintenance programme.
 *
 * The revision supersedes rather than edits: every compliance already signed
 * was signed against a particular revision, and an audit asks which one. The
 * tasks carry across, because re-entering a whole programme to change its
 * revision number is how people end up not revising it at all.
 */
export function reviseProgramme(ctx: ProgrammeContext, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Revise the maintenance programme',
    sub: `${ctx.registration} — currently ${ctx.current?.revision ?? 'none'} with ${ctx.current?.taskCount ?? 0} tasks`,
    primary: 'Issue the revision',
    body: () => html`
      <div class="frow3">
        ${field('New revision', input('revision', '', 'placeholder="rev 5" maxlength="24"'))}
        ${field('Effective from', input('effectiveFrom', todayISO(), 'type="date"'))}
        ${field('Basis', select('basis',
          ctx.allowedBases.map((b) => opt(b, BASIS_LABEL[b] ?? b, b === ctx.current?.basis)).join('')),
          'Usually unchanged')}
      </div>
      <div class="frow">
        ${field('Approved by', input('approvedBy', ctx.current?.approvedBy ?? '', 'placeholder="This organisation, or the authority" maxlength="160"'))}
        ${field('Approved on', input('approvedAt', '', 'type="date"'))}
      </div>
      ${field('Why it is being revised', input('reason', '', 'placeholder="Added CS-STAN modification tasks; oil interval aligned to SI-1014" maxlength="400"'))}
      ${callout('info', 'i', html`
        ${ctx.current?.taskCount ?? 0} task(s) carry forward with their compliance history intact.
        ${esc(ctx.current?.revision ?? 'The current revision')} is superseded, not deleted — a compliance
        signed against it stays traceable to the revision that was in force.
      `)}
      ${ctx.reviewOverdue
        ? callout('caution', '!', html`
            The annual effectiveness review under ML.A.302(c)(9) was due
            ${esc(fmtDate(ctx.reviewDueAt))}. Issuing a revision records the review as done today.
          `)
        : ''}
    `,
    collect: (root) => {
      const revision = val(root, 'revision');
      if (!revision) return { error: 'Give the new revision a number.' };
      if (revision === ctx.current?.revision) {
        return { error: `The programme is already at ${revision}. Use a different number.` };
      }
      const reason = val(root, 'reason');
      if (!reason) return { error: 'Record why it is being revised — it goes on the programme record.' };
      return {
        revision,
        effectiveFrom: val(root, 'effectiveFrom'),
        basis: val(root, 'basis') || undefined,
        approvedBy: val(root, 'approvedBy') || undefined,
        approvedAt: val(root, 'approvedAt') || undefined,
        reason,
      };
    },
    submit: (payload) => api.programme.revise(ctx.aircraftId, payload),
    done: (r) =>
      `${r.revision} issued — ${r.tasksCarriedForward} task(s) carried forward, ${r.supersededRevision} superseded`,
    onDone: reload,
  });
}

const SOURCE_KIND: Array<[string, string]> = [
  ['MM_CHAPTER_5', 'Maintenance manual, chapter 5'],
  ['MPD', 'Maintenance planning document'],
  ['SB', 'Service bulletin or service instruction'],
  ['CMM', 'Component maintenance manual'],
  ['MIP', 'Minimum inspection programme'],
  ['OPERATOR', 'Added by this organisation'],
];

const TASK_KIND: Array<[string, string]> = [
  ['SCHEDULED', 'Repeats at an interval'],
  ['CALENDAR_ONLY', 'Calendar only — no hours'],
  ['ONE_OFF', 'Once, then done'],
  ['LIFE_LIMIT', 'Life limit — replaced, not repeated'],
];

/**
 * Adding or changing one task on the revision that is in force.
 *
 * Not everything is a revision. A service instruction arrives, a task was
 * mistyped, an interval is corrected before the programme is issued — asking
 * for a whole new revision for that is how a programme ends up out of date on
 * paper and correct only in somebody's head. What must not happen is a silent
 * edit to a task that has already been signed against, which is why removing
 * one keeps it (soft delete) and why the revision is named in the dialog.
 */
export function editProgrammeTask(
  aircraftId: string,
  revision: string,
  task: ProgrammeTaskRow | null,
  reload: () => void,
): void {
  const adding = !task;
  openDialog<Record<string, unknown>>({
    wide: true,
    title: adding ? 'Add a task to the programme' : 'Change this task',
    sub: `Revision ${revision} — in force now`,
    primary: adding ? 'Add it' : 'Save the change',
    body: () => html`
      <div class="frow3">
        ${field('Code', input('code', task?.code ?? '', 'placeholder="AMP 05-20-04" style="font-family:var(--mono)" maxlength="40"'),
          'Optional, but it is what the work pack prints')}
        ${field('ATA chapter', input('ataChapter', task?.ataChapter ?? '', 'placeholder="72" style="font-family:var(--mono)" maxlength="8"'))}
        ${field('Where it comes from', select('sourceKind',
          SOURCE_KIND.map(([v, l]) => opt(v, l, v === (task?.sourceKind ?? 'MM_CHAPTER_5'))).join('')))}
      </div>
      ${field('Task', input('title', task?.title ?? '', 'placeholder="Replace the fuel filter element" maxlength="300"'))}
      ${field('Reference', input('sourceReference', task?.sourceReference ?? '', 'placeholder="MML-915 i A, section 05-20-00, p.41" maxlength="200"'),
        'What somebody opens to answer “why is this task here”')}

      <div class="fset">
        <div class="lg">When it falls due</div>
        ${field('Kind', select('taskKind',
          TASK_KIND.map(([v, l]) => opt(v, l, v === (task?.taskKind ?? 'SCHEDULED'))).join('')))}
        <div class="frow3">
          ${field('Every (hours)', input('intervalHours', task?.intervalHours ?? '', 'placeholder="100" inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Every (months)', input('intervalMonths', task?.intervalMonths ?? '', 'placeholder="12" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Every (landings)', input('intervalLandings', task?.intervalLandings ?? '', 'placeholder="—" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        <div class="frow">
          ${field('Tolerance (hours)', input('toleranceHours', task?.toleranceHours ?? '', 'placeholder="0" inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
            'Only what the manual states')}
          ${field('Tolerance (days)', input('toleranceDays', task?.toleranceDays ?? '', 'placeholder="0" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        ${callout('info', 'i', html`
          Give whichever the manual states, or both — with both, whichever falls first drives it. A
          tolerance the manual does not give is margin the aircraft has not earned, so the default is none.
        `)}
      </div>

      ${adding
        ? html`
            <div class="fset">
              <div class="lg">When it was last done</div>
              <div class="frow">
                ${field('Date', input('lastAccomplishedAt', '', 'type="date"'))}
                ${field('At airframe hours', input('lastAccomplishedTsn', '', 'placeholder="412.8" inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
              </div>
              ${callout('caution', '!', html`
                Leave both empty and the interval is measured from <b>today</b>, which on a task that was
                last done two hundred hours ago reports margin it does not have.
              `)}
            </div>
          `
        : ''}

      <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
        <input type="checkbox" name="isPilotOwnerAllowed" ${task?.isPilotOwnerAllowed ? 'checked' : ''} style="margin-top:3px">
        <span>A <b>pilot-owner</b> may do this one — ML.A.803 and Appendix II.</span>
      </label>

      ${callout('info', 'i', html`
        This changes revision ${esc(revision)} itself rather than issuing a new one. Compliance already
        signed stays attached to the task and to the revision it was signed against.
      `)}
    `,
    collect: (root) => {
      const title = val(root, 'title');
      if (!title) return { error: 'The task needs a title — it is what the mechanic reads.' };
      const kind = val(root, 'taskKind');
      // The code shown is the display code — an AD's task shows the directive's
      // own reference, not the key it is filed under. Sending it back unchanged
      // would overwrite the stored key with the pretty version of it, so it
      // goes only when somebody has actually typed something else.
      const code = val(root, 'code');
      const body = {
        code: !code || code === (task?.code ?? '') ? undefined : code,
        title,
        ataChapter: val(root, 'ataChapter') || undefined,
        sourceKind: val(root, 'sourceKind'),
        sourceReference: val(root, 'sourceReference') || undefined,
        taskKind: kind,
        intervalHours: numVal(root, 'intervalHours'),
        intervalMonths: numVal(root, 'intervalMonths'),
        intervalLandings: numVal(root, 'intervalLandings'),
        toleranceHours: numVal(root, 'toleranceHours'),
        toleranceDays: numVal(root, 'toleranceDays'),
        isPilotOwnerAllowed: Boolean(root.querySelector<HTMLInputElement>('[name="isPilotOwnerAllowed"]')?.checked),
        lastAccomplishedAt: val(root, 'lastAccomplishedAt') || undefined,
        lastAccomplishedTsn: numVal(root, 'lastAccomplishedTsn'),
      };
      if (kind === 'SCHEDULED' && !(body.intervalHours || body.intervalMonths || body.intervalLandings)) {
        return { error: 'A repeating task needs an interval. Without one it never becomes due.' };
      }
      return body;
    },
    submit: (payload) =>
      adding
        ? api.programme.addTask(aircraftId, payload)
        : api.programme.updateTask(aircraftId, task!.id, payload),
    done: (r) => `${r.title} — ${adding ? 'added to' : 'changed on'} the programme`,
    onDone: reload,
  });
}

/** The annual review, where nothing needs to change. */
export function recordProgrammeReview(ctx: ProgrammeContext, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Record programme review',
    sub: `${ctx.registration} — ${ctx.current?.revision ?? 'no programme'}`,
    primary: 'Record it',
    body: () => html`
      ${field('Reviewed on', input('reviewedAt', todayISO(), 'type="date"'))}
      ${field('Findings', input('findings', '', 'placeholder="Programme reviewed against utilisation and defect history; no change required." maxlength="2000"'))}
      ${callout('info', 'i', html`
        ML.A.302(c)(9) asks for the review, not for a change. Recording "no change required" is the common
        outcome, and it has to be recordable or the review simply goes unrecorded.
      `)}
    `,
    collect: (root) => {
      const findings = val(root, 'findings');
      if (!findings) return { error: 'Record what the review found, even if it is "no change required".' };
      return { reviewedAt: val(root, 'reviewedAt'), findings };
    },
    submit: (payload) => api.programme.recordReview(ctx.aircraftId, payload),
    done: (r) => `Review recorded — next due ${String(r.nextReviewDue).slice(0, 10)}`,
    onDone: reload,
  });
}

// ── weight and balance ──────────────────────────────────────────────────────

/**
 * Recording a weighing.
 *
 * The empty mass and arm belong to this aircraft and change whenever its
 * equipment does. The moment is computed rather than typed: mass times arm is
 * arithmetic, and a typed moment that disagrees with its own inputs produces a
 * loading sheet with the wrong centre of gravity.
 */
export function recordWeighing(ctx: WbContext, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record weighing',
    sub: `${ctx.registration} — ${ctx.type.name}`,
    primary: 'Record it',
    body: () => html`
      <div class="frow">
        ${field('Report reference', input('reference', '', 'placeholder="WB-YRBUC-08" style="font-family:var(--mono)" maxlength="60"'))}
        ${field('Weighed on', input('weighedAt', todayISO(), 'type="date"'))}
      </div>
      <div class="frow3">
        ${field('Empty mass (kg)', input('emptyMassKg', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('Empty arm (m)', input('emptyArmMetres', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('MTOM (kg)', input('mtomKg', ctx.type.mtomKg ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
      </div>
      ${field('Why it was reweighed', input('reason', '', 'placeholder="After ADS-B installation" maxlength="200"'))}
      ${callout('info', 'i', html`
        The moment is computed from mass × arm rather than entered.
        ${ctx.current
          ? `The current report ${ctx.current.reference} (${ctx.current.emptyMassKg} kg) is superseded, not replaced — a loading sheet computed against it was correct at the time.`
          : 'This will be the first weighing on file for this aircraft.'}
      `)}
    `,
    collect: (root) => {
      const reference = val(root, 'reference');
      if (!reference) return { error: 'Give the weighing report a reference.' };
      const emptyMassKg = numVal(root, 'emptyMassKg');
      const emptyArmMetres = numVal(root, 'emptyArmMetres');
      const mtomKg = numVal(root, 'mtomKg');
      if (emptyMassKg === undefined || emptyArmMetres === undefined || mtomKg === undefined) {
        return { error: 'Empty mass, empty arm and MTOM are all required.' };
      }
      if (emptyMassKg >= mtomKg) return { error: 'The empty mass is at or above the maximum take-off mass.' };
      return {
        reference,
        weighedAt: val(root, 'weighedAt'),
        emptyMassKg,
        emptyArmMetres,
        mtomKg,
        reason: val(root, 'reason') || undefined,
      };
    },
    submit: (payload) => api.weightBalance.recordWeighing(ctx.aircraftId, payload),
    done: (r) => `${r.reference} recorded — useful load ${r.usefulLoadKg} kg`,
    onDone: reload,
  });
}

/**
 * Defining the loading stations.
 *
 * These belong to the TYPE, not the aircraft — every 172S has the same seat
 * arms — so the dialog says so plainly. Editing them here changes the loading
 * model for every aircraft of the type.
 */
export function defineStations(ctx: WbContext, reload: () => void): void {
  let rows = ctx.stations.length
    ? ctx.stations.map((s) => ({ code: s.code, label: s.label, arm: s.armMetres ?? 0, max: s.maxMassKg, fuel: s.isFuel }))
    : [
        { code: 'FRONT_SEATS', label: 'Front seats', arm: 0.94, max: 200, fuel: false },
        { code: 'REAR_SEATS', label: 'Rear seats', arm: 1.85, max: 200, fuel: false },
        { code: 'BAGGAGE_A', label: 'Baggage area A', arm: 2.31, max: 54, fuel: false },
        { code: 'FUEL', label: 'Fuel', arm: 1.17, max: null, fuel: true },
      ];

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Loading stations',
    sub: `${ctx.type.name} — applies to every aircraft of this type`,
    primary: 'Save stations',
    body: () => html`
      ${callout('caution', '!', html`
        Stations belong to the <b>type</b>, not to ${esc(ctx.registration)}. Changing them changes the
        loading model for every aircraft of this type in the fleet.
      `)}
      ${rows
        .map(
          (r, i) => html`
            <div class="frow3">
              ${field(i === 0 ? 'Code' : '', input(`code_${i}`, r.code, 'style="font-family:var(--mono)" maxlength="24"'))}
              ${field(i === 0 ? 'Label' : '', input(`label_${i}`, r.label, 'maxlength="120"'))}
              ${field(i === 0 ? 'Arm (m)' : '', input(`arm_${i}`, r.arm, 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
            </div>
          `,
        )
        .join('')}
      ${field('Add another station', input('newCode', '', 'placeholder="Leave blank to keep the list as it is" maxlength="24"'))}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLInputElement>('[name="newCode"]')?.addEventListener('change', (e) => {
        const code = (e.target as HTMLInputElement).value.trim();
        if (!code) return;
        rows = [...rows, { code, label: code.replace(/_/g, ' ').toLowerCase(), arm: 1, max: null, fuel: false }];
        // The dialog carries typed values across a refresh, which is right
        // everywhere except here: this box has just been consumed into a row,
        // and leaving the code in it invites adding the same station twice.
        (e.target as HTMLInputElement).value = '';
        refresh();
        const box = root.querySelector<HTMLInputElement>('[name="newCode"]');
        if (box) box.value = '';
      });
    },
    collect: (root) => {
      const stations = rows.map((_, i) => ({
        code: val(root, `code_${i}`),
        label: val(root, `label_${i}`),
        armMetres: numVal(root, `arm_${i}`) ?? 0,
        maxMassKg: rows[i].max ?? undefined,
        isFuel: rows[i].fuel,
      })).filter((s) => s.code && s.label);

      if (!stations.length) return { error: 'Define at least one station.' };
      const codes = new Set(stations.map((s) => s.code));
      if (codes.size !== stations.length) return { error: 'Two stations share a code.' };
      return { stations };
    },
    submit: (payload) => api.weightBalance.defineStations(ctx.aircraftId, payload),
    done: (r) => `${r.stations} station(s) defined for ${r.typeModel}`,
    onDone: reload,
  });
}
